import React, { useState, useEffect } from 'react';
import { Form, Button, Alert } from '@/components/ui';
import { workIdService, type WorkIdDetail } from '../../../services/workIdService';
import ResponsiveModal from '../../../components/ResponsiveModal';

type TaskWithType = WorkIdDetail['tasks'][number];

interface WorkIdUnassignModalProps {
  visible: boolean;
  onClose: () => void;
  workId: WorkIdDetail | null;
  onSuccess: () => void;
}

const WorkIdUnassignModal: React.FC<WorkIdUnassignModalProps> = ({
  visible,
  onClose,
  workId,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);

  useEffect(() => {
    if (visible && workId) {
      setError(null);
      const tasks = workId.tasks ?? [];
      if (tasks.length === 1) {
        setSelectedTaskId(tasks[0].id);
      } else {
        setSelectedTaskId(null);
      }
    }
  }, [visible, workId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workId || selectedTaskId == null) return;

    setError(null);
    setSubmitting(true);

    try {
      await workIdService.unassign(workId.id, selectedTaskId);
      onClose();
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove Work ID from task');
    } finally {
      setSubmitting(false);
    }
  };

  if (!workId) return null;

  const tasks = workId.tasks ?? [];
  const taskToUnassign = selectedTaskId != null ? tasks.find((t) => t.id === selectedTaskId) : null;
  const hasSingleTask = tasks.length === 1;
  const canSubmit = selectedTaskId != null;

  const title = `Remove Work ID from Task${tasks.length > 1 ? 's' : ''} - ${workId.workIdentificationId ?? workId.id}`;

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={title}
      size="medium"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="warning"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
          >
            {submitting ? 'Removing…' : 'Remove from Task'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}

        <p className="tw-text-content-muted tw-mb-3">
          Removing this Work ID from the task will return the Work ID to <strong>Approved</strong>{' '}
          status. The task will no longer be linked to this Work ID.
        </p>

        {hasSingleTask && taskToUnassign ? (
          <p className="tw-mb-0">
            Remove Work ID from task{' '}
            <strong>
              {taskToUnassign.taskNumber ?? taskToUnassign.id} ({taskToUnassign.type ?? 'Task'})
            </strong>
            ?
          </p>
        ) : tasks.length > 1 ? (
          <div className="tw-mb-0">
            <Form.Label>Select task to remove Work ID from:</Form.Label>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {tasks.map((task: TaskWithType) => (
                <Form.Check
                  key={task.id}
                  type="radio"
                  name="unassignTask"
                  id={`unassign-task-${task.id}`}
                  label={`${task.taskNumber ?? task.id} (${task.type ?? 'Task'})`}
                  checked={selectedTaskId === task.id}
                  onChange={() => setSelectedTaskId(task.id)}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="tw-text-content-muted tw-mb-0">No linked tasks to unassign.</p>
        )}
      </form>
    </ResponsiveModal>
  );
};

export default WorkIdUnassignModal;
